<script setup>
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import BasePage from "@/components/Layouts/BasePage.vue";
import EntityForm from "@/components/common/Form.vue";
import ButtonSave from "@/components/common/button/ButtonSave.vue";
import { CollaboratorEntity } from "@/entities/CollaboratorEntity.js";
import { getCollaboratorById, saveCollaborator } from "@/services/api/collaborators.js";
import { getCorpUsers } from "@/services/api/corporation.js";
import { getUserById } from "@/services/api/users.js";
import { buildPayloadFromEntity, searchAndSetOptions } from "@/utils/index.js";
import { formatDate } from "@/utils/dateTimeFormat.js";
import { handleError } from "@/utils/errors.js";
import router from "@/router/index.js";

const { t } = useI18n();
const route = useRoute();
const id = route.params.id;
const entity = ref(new CollaboratorEntity());
const formData = ref(entity.value.getDefaultValues());
const loading = ref(false);
const isMounted = ref(false);
const isFetching = ref(false);
const form = ref(null);

function configureCreateMode() {
    if (id) return;

    formData.value.status = true;
}

function isActiveStatus(status) {
    return status === true || status === "ativo";
}

function normalizeDateForPayload(dateValue) {
    if (!dateValue) return "";

    if (dateValue instanceof Date) {
        return formatDate(dateValue, "YYYY-MM-DD");
    }

    if (typeof dateValue === "string" && dateValue.includes("/")) {
        const [day, month, year] = dateValue.split("/");
        return `${year}-${month}-${day}`;
    }

    return String(dateValue).slice(0, 10);
}

function mapUserOption(user) {
    return {
        label: user.name,
        value: user.id
    };
}

function createSelectedOption(item, titleKey = "nome") {
    if (!item) return null;

    const value = item.id ?? item.value;
    const title = item[titleKey] ?? item.name ?? item.nome ?? item.title ?? item.label;

    if (!value || !title) return null;

    return { title, value };
}

function setAutocompleteOption(key, selectedOption) {
    if (!selectedOption) return;

    const field = entity.value.fields.find(field => field.key === key);
    if (field) {
        field.options = [{
            label: selectedOption.title,
            value: selectedOption.value
        }];
    }
}

async function getSelectedUserOption(data) {
    const relatedUser = data.user ?? data.usuario;
    const selectedFromResponse = createSelectedOption(relatedUser, "name");

    if (selectedFromResponse || !data.user_id) {
        return selectedFromResponse;
    }

    const response = await getUserById({ id: data.user_id });
    const selectedUser = response?.data ?? response;

    return createSelectedOption(selectedUser, "name");
}

async function onSave() {
    try {
        const { valid } = await form.value.validate();
        if (!valid) return;

        loading.value = true;
        formData.value.id = id ?? null;
        formData.value.data_admissao = normalizeDateForPayload(formData.value.data_admissao);

        const payload = buildPayloadFromEntity({
            entity: entity.value,
            data: formData.value
        });

        payload.id = formData.value.id ?? null;
        payload.status = id && !isActiveStatus(formData.value.status) ? "inativo" : "ativo";

        await saveCollaborator(payload);
        message.success(t("common.messages.success.save"));
        await router.push(entity.value.pages.list.route?.at(-1));
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t, formData.value?.nome));
    } finally {
        loading.value = false;
    }
}

async function handleSearch({ handler, query }) {
    const handlers = {
        handleSearchUser: () =>
            searchAndSetOptions({
                key: "user_id",
                apiFn: getCorpUsers,
                query,
                entity,
                mapFn: mapUserOption,
                isFetching,
                searchParam: "name"
            }),
    };

    if (handlers[handler]) {
        await handlers[handler]();
    } else {
        console.warn(`Handler não encontrado: ${handler}`);
    }
}

onMounted(async () => {
    try {
        configureCreateMode();

        if (id) {
            const response = await getCollaboratorById({ id });
            const data = response.data ?? response;
            const userOption = await getSelectedUserOption(data);

            setAutocompleteOption("user_id", userOption);

            formData.value = {
                ...entity.value.getDefaultValues(),
                ...data,
                status: isActiveStatus(data.status),
                user_id: userOption,
            };
        }
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        isMounted.value = true;
    }
});
</script>

<template>
    <BasePage :loading-mounted="isMounted" :entity="entity">
        <template v-if="isMounted" #default>
            <EntityForm :entity="entity" v-model="formData" ref="form" @search="handleSearch" />

            <ButtonSave :loading="loading" @click="onSave" />
        </template>
    </BasePage>
</template>
